import { z } from "zod";
import type { AcCardStatus } from "./accreditation";
import type { AccreditationCategoryRef, AccreditationOrgRef, AccreditationRef } from "./accreditation-records";
import { PAGE_SIZE_MAX, searchParam } from "./pagination";
import type { ClusterRef, TransportDisplayStatus } from "./transport";

/**
 * Travel (M9): one arrival and one departure per CARD (`travel_legs`,
 * `UNIQUE (ac_card_id, direction)`), the real flights they ride
 * (`flights`, one row per carrier + number + service date, shared by every
 * passenger), and the entry points people come through (`entry_points`).
 * Wire shapes only (snake_case). Plan: `~/.claude/plans/m9-travel/PLAN.md`.
 *
 * The database CHECKs on `travel_legs.direction` / `mode`,
 * `entry_points.type`, `flights.status` / `tracking` are generated from the
 * tuples below (migration `0023_travel`, PLAN's "0021"); a parity test holds them equal.
 *
 * Rules both sides lean on:
 *   · A leg's `mode` equals its entry point's `type` (an AIR leg lands at an
 *     AIR entry point). The schema cannot see the entry point: the API
 *     refuses a mismatch with 422 VALIDATION_FAILED on `entry_point_id`.
 *   · Only AIR legs carry `flight_id` (DB CHECK too). A leg ON a flight takes
 *     its times from the flight; `delayed_to` is the manual delay of a leg
 *     with no flight row (land, sea, or flight tracking off).
 *   · `travel_date` is the local date in `games.timezone`; `scheduled_at` is
 *     optional (the date may be known before the time).
 *   · A leg is always sent WHOLE: an omitted optional field is stored as NULL.
 *   · Flight tracking is an accelerator, never a gate: with the provider off,
 *     people type the flight into `service_no` and the time into `scheduled_at`.
 *   · Transit (user, 2026-10-15): an AIR leg on a flight row may carry up to
 *     TRAVEL_CONNECTIONS_MAX connecting flights (`travel_leg_connections`,
 *     0031). The MAIN flight stays `flight_id` — the one that lands at
 *     (ARRIVAL) / leaves from (DEPARTURE) a Games airport; pickups, stays and
 *     every cascade key on it alone. `connections` are in TRAVEL ORDER: for an
 *     ARRIVAL the feeders flown BEFORE the main flight (first flown first), for
 *     a DEPARTURE the onward flights AFTER it. Each flight must leave from the
 *     airport the previous one lands at, no earlier than it lands (422
 *     CONNECTION_ROUTE_MISMATCH / CONNECTION_TIME_ORDER). Like every other
 *     field, `connections` is part of the WHOLE leg: omitted = none.
 *     Too tight a change is never refused: `connection_at_risk` (computed on
 *     read, never stored) flags it.
 */

// ── Vocabulary ──────────────────────────────────────────────────────────────

export const TRAVEL_DIRECTIONS = ["ARRIVAL", "DEPARTURE"] as const;
export type TravelDirection = (typeof TRAVEL_DIRECTIONS)[number];

/** The `:direction` segment of `/accreditations/:id/travel/:direction`. */
export const TRAVEL_DIRECTION_SEGMENTS = { ARRIVAL: "arrival", DEPARTURE: "departure" } as const satisfies Record<TravelDirection, string>;
export type TravelDirectionSegment = (typeof TRAVEL_DIRECTION_SEGMENTS)[TravelDirection];

/** How the person travels on this leg. */
export const TRAVEL_MODES = ["AIR", "LAND", "SEA"] as const;
export type TravelMode = (typeof TRAVEL_MODES)[number];

/**
 * How they travel within a mode (user, 2026-10-31): AIR = a commercial flight
 * or a private / cargo one; LAND = car or train; SEA = cruise or commercial
 * vessel; any mode = OTHER, with the kind typed (`transport_type_other`).
 * `travel_legs.transport_type` (0071) — NULL on legs saved before it. The DB
 * CHECKs are built from these lists.
 */
export const TRAVEL_TRANSPORT_TYPES = ["COMMERCIAL_FLIGHT", "PRIVATE_CARGO", "CAR", "TRAIN", "CRUISE", "COMMERCIAL_VESSEL", "OTHER"] as const;
export type TravelTransportType = (typeof TRAVEL_TRANSPORT_TYPES)[number];
export const TRAVEL_TRANSPORT_TYPES_BY_MODE = {
	AIR: ["COMMERCIAL_FLIGHT", "PRIVATE_CARGO", "OTHER"],
	LAND: ["CAR", "TRAIN", "OTHER"],
	SEA: ["CRUISE", "COMMERCIAL_VESSEL", "OTHER"],
} as const satisfies Record<TravelMode, readonly TravelTransportType[]>;
/** The type a new leg of that mode starts on. */
export const defaultTransportType = (mode: TravelMode): TravelTransportType => TRAVEL_TRANSPORT_TYPES_BY_MODE[mode][0];
/** May a leg of `mode` take `type`? NULL (an older leg) always may. */
export const transportTypeFits = (mode: TravelMode, type: TravelTransportType | null): boolean =>
	type === null || (TRAVEL_TRANSPORT_TYPES_BY_MODE[mode] as readonly TravelTransportType[]).includes(type);
/** An AIR leg names its flight number unless it is a private / cargo or other flight. */
export const legNeedsFlightNumber = (mode: TravelMode, type: TravelTransportType | null): boolean => mode === "AIR" && (type === null || type === "COMMERCIAL_FLIGHT");
export const TRAVEL_TRANSPORT_TYPE_OTHER_MAX = 80;

/** An airport, a land crossing (CIQ), a ferry terminal. */
export const ENTRY_POINT_TYPES = ["AIR", "LAND", "SEA"] as const;
export type EntryPointType = (typeof ENTRY_POINT_TYPES)[number];

/**
 * A flight's status in our vocabulary (the provider's is mapped onto it in
 * `apps/api/src/lib/flight-status.ts`):
 *   SCHEDULED  not departed yet, on time as far as anyone knows
 *   DELAYED    not departed yet, later than scheduled (by ≥ FLIGHT_DELAY_THRESHOLD_MINUTES)
 *   DEPARTED   in the air
 *   LANDED     arrived
 *   CANCELLED  will not fly
 *   DIVERTED   landed somewhere else
 *   UNKNOWN    no word: tracking found nothing, or gave up 12 h after the expected landing
 */
export const FLIGHT_STATUSES = ["SCHEDULED", "DELAYED", "DEPARTED", "LANDED", "CANCELLED", "DIVERTED", "UNKNOWN"] as const;
export type FlightStatus = (typeof FLIGHT_STATUSES)[number];

/** Statuses that never change again: the tracker stops polling (the partial index on `next_check_at` excludes them). */
export const FLIGHT_FINAL_STATUSES = ["LANDED", "CANCELLED", "DIVERTED"] as const satisfies readonly FlightStatus[];
export const isFlightFinal = (s: FlightStatus): boolean => (FLIGHT_FINAL_STATUSES as readonly FlightStatus[]).includes(s);

/**
 * Who keeps a flight's times:
 *   PROVIDER  the tracker job polls the flight-data API
 *   MANUAL    a host desk overrode it (`PATCH /flights/:id`); tracking is
 *             paused until `POST /flights/:id/resume-tracking`
 */
export const FLIGHT_TRACKING = ["PROVIDER", "MANUAL"] as const;
export type FlightTracking = (typeof FLIGHT_TRACKING)[number];

/**
 * Why `GET /flights/lookup` found nothing (200, `found: false`):
 *   TRACKING_OFF  no flight-data provider is configured — type the time instead
 *   NOT_FOUND     the provider does not know this flight on that date
 *   NOT_OUR_AIRPORT  (lookup only) the flight operates that date, but no leg
 *                 lands at one of our airports (ARRIVAL) / leaves one
 *                 (DEPARTURE); `route` says where it flies, `airports` are
 *                 ours. Nothing is stored. The dates endpoint cannot know
 *                 routes, so a picked operating day can still answer this.
 * A provider that does not answer is 503 SERVICE_UNAVAILABLE, not a miss.
 */
export const FLIGHT_LOOKUP_MISS_REASONS = ["TRACKING_OFF", "NOT_FOUND", "NOT_OUR_AIRPORT"] as const;
export type FlightLookupMissReason = (typeof FLIGHT_LOOKUP_MISS_REASONS)[number];

/**
 * What a `GET /flights/lookup` is for (`role=`):
 *   MAIN        the leg's own flight — it must land at (ARRIVAL) / leave from
 *               (DEPARTURE) one of our airports, else NOT_OUR_AIRPORT (default)
 *   CONNECTION  a connecting flight of a transit leg (the MNL→BKI feeder of a
 *               BKI→KUL arrival): no airport of ours required, never
 *               NOT_OUR_AIRPORT, no suggested entry point. Stored like any flight.
 */
export const FLIGHT_LOOKUP_ROLES = ["MAIN", "CONNECTION"] as const;
export type FlightLookupRole = (typeof FLIGHT_LOOKUP_ROLES)[number];

// ── Limits ──────────────────────────────────────────────────────────────────

/** `POST /travel/bulk`: card ids per request. */
export const TRAVEL_BULK_MAX = 500;
/** `GET /travel/groups` / `/travel/legs`: `from`..`to` spans at most this many days, both ends included. */
export const TRAVEL_GROUP_RANGE_DAYS_MAX = 3;
/** `GET /travel/groups` answers at most this many groups, then `truncated: true` (a guard, not a page). */
export const TRAVEL_GROUPS_LIMIT = 500;
/** Organisations named per group; the rest are counted in `organizations_more`. */
export const TRAVEL_GROUP_ORGS_MAX = 5;
/** `GET /travel/legs` default page size (max PAGE_SIZE_MAX). */
export const TRAVEL_LEGS_PAGE_SIZE_DEFAULT = 20;
/** The opaque `group` key of a group, as `/travel/legs?group=` takes it back. */
export const TRAVEL_GROUP_KEY_MAX = 200;

/** Bus / ferry / train name, or the flight as typed when there is no flight row. */
export const TRAVEL_SERVICE_NO_MAX = 40;
/** Where the person comes from (ARRIVAL) or goes to (DEPARTURE). */
export const TRAVEL_ORIGIN_MAX = 120;
export const TRAVEL_REMARKS_MAX = 500;
/** The audit headline a caller may add to a flight override. */
export const TRAVEL_REASON_MAX = 300;

/** A flight is DELAYED once its expected time is this much later than scheduled. */
export const FLIGHT_DELAY_THRESHOLD_MINUTES = 15;
/** Connecting flights per leg, besides the main flight (`travel_leg_connections.seq` 1..max, DB CHECK). */
export const TRAVEL_CONNECTIONS_MAX = 2;
/**
 * A connection is AT RISK when the next flight is expected to leave less than
 * this long after the previous one is expected to land (`connection_at_risk`).
 */
export const MIN_CONNECTION_MINUTES = 60;
/** What a person may type as a flight number ("MH 803", "ak-5101a"). */
export const FLIGHT_DESIGNATOR_INPUT_MAX = 12;

/** Immigration + bags: pickup = expected landing + dwell. */
export const ENTRY_POINT_DWELL_MINUTES_MIN = 0;
export const ENTRY_POINT_DWELL_MINUTES_MAX = 600;
export const ENTRY_POINT_DWELL_MINUTES_DEFAULT = 60;
/** Check-in and security: drop-off = departure − lead. */
export const ENTRY_POINT_LEAD_MINUTES_MIN = 0;
export const ENTRY_POINT_LEAD_MINUTES_MAX = 720;
export const ENTRY_POINT_LEAD_MINUTES_DEFAULT = 180;
export const ENTRY_POINT_CODE_MAX = 20;
export const ENTRY_POINT_NAME_MAX = 120;
/** Matches the provider's arrival terminal ("1", "2", "T2"). */
export const ENTRY_POINT_TERMINAL_MAX = 10;
export const ENTRY_POINT_SORT_ORDER_MAX = 9999;
/** A short admin list (tens of rows): one page of 50 normally holds all of it. */
export const ENTRY_POINT_PAGE_SIZE_DEFAULT = 50;
/**
 * M9b (migration 0032): people the entry point's welcome desk can process per
 * hour; NULL = not known. The busiest-hour bar is drawn against it and
 * `/travel/attention` counts hours over it. DB CHECK: NULL or MIN..MAX.
 */
export const ENTRY_POINT_DESK_CAPACITY_MIN = 1;
export const ENTRY_POINT_DESK_CAPACITY_MAX = 100_000;

/**
 * M9b: the readiness atoms of an ARRIVAL leg (`notReadySql` in the API,
 * `journeyReadiness()` in `travel-journeys.ts`):
 *   card      the card is not APPROVED yet (transport and rooms wait for it)
 *   transfer  it needs transport and has no live transfer with a driver
 *   recheck   its live transfer has a driver, but the delay cascade flagged
 *             the trip (`needs_recheck`: the pickup moved off the driver's
 *             shift; `needs_redispatch`: a vehicle was released) — the driver
 *             is not CONFIRMED until dispatch re-checks it
 *             (TRANSFER_RECHECK_STATUSES). Disjoint from `transfer`.
 *   bed       it has no open room assignment
 * `GET /travel/legs?readiness=` and `/travel/journeys?not_ready=` take them.
 */
export const TRAVEL_READINESS_ATOMS = ["card", "transfer", "recheck", "bed"] as const;
export type TravelReadinessAtom = (typeof TRAVEL_READINESS_ATOMS)[number];

// ── Patterns ────────────────────────────────────────────────────────────────
// One copy each: the zod schemas below test against them, and the database
// CHECKs are built from them (`matchesPattern()` in apps/api/src/db/schema/
// {entry-points,flights}.ts). Plain character classes only (no `\d`, no
// groups): `.source` is written into the SQL as it stands.

/** An entry point's code: `KLIA2`, `JHB-CIQ` (upper-case; ≤ ENTRY_POINT_CODE_MAX). `ck_entry_points_code`. */
export const ENTRY_POINT_CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]*$/;
/** An airport's IATA code: `KUL`. `ck_entry_points_iata_code`, `ck_flights_iata`. */
export const IATA_CODE_PATTERN = /^[A-Z]{3}$/;
/** An IATA airline designator: `MH`, `5J`. `ck_flights_carrier_code` (the parser also wants a letter in it). */
export const CARRIER_CODE_PATTERN = /^[A-Z0-9]{2}$/;
/** 1–4 digits and an optional suffix letter: `803`, `5101A`. `ck_flights_flight_number` (the parser also drops leading zeros). */
export const FLIGHT_NUMBER_PATTERN = /^[0-9]{1,4}[A-Z]?$/;

// ── Flight designators: one parser for both sides ───────────────────────────

/** A flight as the provider and the `flights` table key it (DB CHECKs: CARRIER_CODE_PATTERN, FLIGHT_NUMBER_PATTERN). */
export interface FlightDesignator {
	/** IATA airline designator, two characters, at least one a letter: `MH`, `AK`, `5J`, `3K`. */
	carrier_code: string;
	/** 1–4 digits without leading zeros, plus an optional operational suffix letter: `803`, `5101A`. */
	flight_number: string;
}

/**
 * Reads a flight number the way people type it: `MH 803`, `mh803`, `MH-0803`,
 * `AK5101A`, `5J 123` → `{ carrier_code, flight_number }`; anything else →
 * null. Leading zeros are dropped (`MH 0803` and `MH 803` are the same
 * flight, so they must be the same row). ICAO codes (`MAS803`) and numbers
 * with no airline are refused: the provider keys flights by IATA designator.
 */
export function parseFlightDesignator(input: string): FlightDesignator | null {
	if (input.length > FLIGHT_DESIGNATOR_INPUT_MAX * 2) return null;
	const s = input.trim().toUpperCase();
	// A space or hyphen marks where the airline ends (`M 803` is not `M8 03`); without one, the airline is the first two characters.
	const split = /^([A-Z0-9]+)[\s-]+([A-Z0-9]+)$/.exec(s);
	const [carrier, rest] = split ? [split[1]!, split[2]!] : /^[A-Z0-9]+$/.test(s) ? [s.slice(0, 2), s.slice(2)] : ["", ""];
	if (!CARRIER_CODE_PATTERN.test(carrier) || !/[A-Z]/.test(carrier)) return null;
	if (!FLIGHT_NUMBER_PATTERN.test(rest)) return null;
	// FLIGHT_NUMBER_PATTERN: digits, then at most one letter.
	const suffix = /[A-Z]$/.test(rest) ? rest.slice(-1) : "";
	const digits = suffix ? rest.slice(0, -1) : rest;
	const n = Number(digits);
	if (n === 0) return null;
	return { carrier_code: carrier, flight_number: `${n}${suffix}` };
}

/** `MH 803` — how a flight is printed everywhere. */
export const formatFlightDesignator = (carrierCode: string, flightNumber: string): string => `${carrierCode} ${flightNumber}`;

// ── Messages (message-factory pattern) ──────────────────────────────────────

export interface TravelFormMessages {
	required: string;
	textTooLong: string;
	dateInvalid: string;
	dateTimeInvalid: string;
	/** Not a flight number we can read (`parseFlightDesignator`). */
	flightInvalid: string;
	/** A LAND / SEA leg named a flight. */
	flightOnlyForAir: string;
	/** A leg on a tracked flight takes its delay from the flight. */
	delayWithFlight: string;
	/** Departure before arrival (both legs in one body). */
	departureBeforeArrival: string;
	/** `to` before `from`. */
	rangeInvalid: string;
	rangeTooLong: string;
	idsDuplicate: string;
	nothingToChange: string;
	/** Actual landing before actual take-off. */
	landedBeforeDeparted: string;
	codeInvalid: string;
	nameRequired: string;
	iataInvalid: string;
	iataOnlyForAir: string;
	minutesInvalid: string;
	sortOrderInvalid: string;
	/** M10. Optional so existing callers compile; defaults to the English copy. */
	clusterInvalid?: string;
	/** An AIR leg with neither a flight number (`service_no`) nor a flight row. Optional; falls back to `required`. */
	flightNumberRequired?: string;
	/** Connections on a leg with no main flight row (or not AIR). Optional; English default. */
	connectionsNeedFlight?: string;
	/** More than TRAVEL_CONNECTIONS_MAX connections. Optional; English default. */
	connectionsTooMany?: string;
	/** M9b: desk capacity not a whole number in ENTRY_POINT_DESK_CAPACITY_MIN..MAX. Optional; English default. */
	deskCapacityInvalid?: string;
	/** The same flight twice in the leg (main + connections). Optional; English default. */
	connectionsDuplicate?: string;
	/** A transport type of another mode (a car on an AIR leg). Optional; English default. */
	transportTypeMismatch?: string;
	/** "Others" picked with nothing typed. Optional; English default. */
	transportTypeOtherRequired?: string;
}

export const enTravelFormMessages: TravelFormMessages = {
	required: "Required",
	textTooLong: "Too long",
	dateInvalid: "Enter a valid date",
	dateTimeInvalid: "Enter a valid date and time",
	flightInvalid: "Enter a flight number like MH 803",
	flightOnlyForAir: "Only air travel has a flight",
	delayWithFlight: "This flight's delay comes from the flight itself",
	departureBeforeArrival: "Departure must be on or after the arrival date",
	rangeInvalid: "The end date must be on or after the start date",
	rangeTooLong: `At most ${TRAVEL_GROUP_RANGE_DAYS_MAX} days`,
	idsDuplicate: "A record is listed twice",
	nothingToChange: "Nothing to change",
	landedBeforeDeparted: "Landing must be after take-off",
	codeInvalid: "Use letters, digits, - or _ (at most 20)",
	nameRequired: "Enter a name",
	iataInvalid: "Enter a 3-letter IATA code",
	iataOnlyForAir: "Only an airport has an IATA code",
	minutesInvalid: "Enter whole minutes",
	sortOrderInvalid: "Enter a whole number, 0 or more",
	flightNumberRequired: "Enter the flight number",
	connectionsNeedFlight: "Look up the main flight before adding connecting flights",
	connectionsTooMany: `At most ${TRAVEL_CONNECTIONS_MAX} connecting flights`,
	connectionsDuplicate: "The same flight is listed twice",
	deskCapacityInvalid: "Enter a whole number from 1 to 100,000",
	transportTypeMismatch: "Pick a type of this mode of travel",
	transportTypeOtherRequired: "Say how they travel",
};

type M = TravelFormMessages;

// ── Field builders (private) ────────────────────────────────────────────────

const uuid = z.string().uuid();

const isRealDate = (v: string) => {
	const d = new Date(`${v}T00:00:00Z`);
	return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};
/** `YYYY-MM-DD`, a real calendar date. Trimmed first (bug 5). */
const dateField = (m: M) => z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, m.dateInvalid).refine(isRealDate, m.dateInvalid);
/** Days from `a` to `b` (both `YYYY-MM-DD`). */
const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
/** An instant with its offset; omitted or null → null. */
const nullableDateTime = (m: M) =>
	z
		.string()
		.trim()
		.datetime({ offset: true, message: m.dateTimeInvalid })
		.nullable()
		.optional()
		.transform((v) => v ?? null);
/** Optional text: trimmed, then ≤ max; blank, null or omitted → null. */
const nullableText = (m: M, max: number) =>
	z
		.string()
		.trim()
		.max(max, m.textTooLong)
		.nullable()
		.optional()
		.transform((v) => (v === undefined || v === null || v === "" ? null : v));
/** A PATCH field: omitted leaves it alone, null or blank clears it. */
const patchText = (m: M, max: number) =>
	z
		.string()
		.trim()
		.max(max, m.textTooLong)
		.transform((v) => (v === "" ? null : v))
		.nullable()
		.optional();
const minutes = (m: M, min: number, max: number) => z.number({ invalid_type_error: m.minutesInvalid }).int(m.minutesInvalid).min(min, m.minutesInvalid).max(max, m.minutesInvalid);
const sortOrder = (m: M) => z.number({ invalid_type_error: m.sortOrderInvalid }).int(m.sortOrderInvalid).min(0, m.sortOrderInvalid).max(ENTRY_POINT_SORT_ORDER_MAX, m.sortOrderInvalid);
/** `"true"` / `"false"` off the query string — never `z.coerce.boolean()` (bug 2). */
const booleanParam = z.enum(["true", "false"]).transform((v) => v === "true");
/** M9b: desk capacity, a whole number in range (null clears, where the caller allows it). */
const deskCapacity = (m: M) => {
	const msg = m.deskCapacityInvalid ?? enTravelFormMessages.deskCapacityInvalid!;
	return z.number({ invalid_type_error: msg }).int(msg).min(ENTRY_POINT_DESK_CAPACITY_MIN, msg).max(ENTRY_POINT_DESK_CAPACITY_MAX, msg);
};

/** Shared with `travel-journeys.ts` (one copy of each field rule). */
export { dateField as travelDateField, daysBetween as travelDaysBetween };

// ── A travel leg ────────────────────────────────────────────────────────────

function legShape(m: M) {
	return {
		mode: z.enum(TRAVEL_MODES, { errorMap: () => ({ message: m.required }) }),
		entry_point_id: z.string({ required_error: m.required }).uuid(m.required),
		travel_date: dateField(m),
		/** The time, when known. */
		scheduled_at: nullableDateTime(m),
		/** Manual delay of a leg with no flight row (land, sea, tracking off). */
		delayed_to: nullableDateTime(m),
		/** How they travel within the mode (`TRAVEL_TRANSPORT_TYPES_BY_MODE`); null = not said (legs saved before 0071). */
		transport_type: z
			.enum(TRAVEL_TRANSPORT_TYPES, { errorMap: () => ({ message: m.transportTypeMismatch ?? enTravelFormMessages.transportTypeMismatch! }) })
			.nullable()
			.optional()
			.transform((v) => v ?? null),
		/** OTHER only: what it is, as typed. Ignored (stored NULL) for any other type. */
		transport_type_other: nullableText(m, TRAVEL_TRANSPORT_TYPE_OTHER_MAX),
		/** AIR only: a `flights` row, from `GET /flights/lookup`. A commercial AIR leg needs this or `service_no` (`legNeedsFlightNumber`). */
		flight_id: uuid.nullable().optional().transform((v) => v ?? null),
		service_no: nullableText(m, TRAVEL_SERVICE_NO_MAX),
		origin: nullableText(m, TRAVEL_ORIGIN_MAX),
		/** Ask for a transfer (M10 suggests one). */
		needs_transport: z.boolean().default(true),
		remarks: nullableText(m, TRAVEL_REMARKS_MAX),
		/**
		 * Transit: connecting flights (`flights` rows, from `GET
		 * /flights/lookup?role=CONNECTION`), in TRAVEL ORDER — see the header.
		 * Part of the whole leg: omitted or `[]` = none.
		 */
		connections: z
			.array(z.object({ flight_id: uuid }))
			.max(TRAVEL_CONNECTIONS_MAX, m.connectionsTooMany ?? enTravelFormMessages.connectionsTooMany)
			.optional()
			.transform((v) => v ?? []),
	};
}

/**
 * An AIR leg names its flight (user, 2026-10-15): a flight row (`flight_id`,
 * from a lookup) or the number as ticketed (`service_no`), else the issue is
 * on `service_no`. Checked before release: no stored AIR leg lacked both, so
 * re-sending an unchanged leg cannot trip it.
 */
const refineLeg = (m: M) => (
	v: {
		mode: TravelMode;
		transport_type: TravelTransportType | null;
		transport_type_other: string | null;
		flight_id: string | null;
		delayed_to: string | null;
		service_no: string | null;
		connections: Array<{ flight_id: string }>;
	},
	ctx: z.RefinementCtx,
) => {
	if (v.connections.length > 0 && (v.mode !== "AIR" || v.flight_id === null))
		ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["connections"], message: m.connectionsNeedFlight ?? enTravelFormMessages.connectionsNeedFlight! });
	else if (new Set([v.flight_id, ...v.connections.map((c) => c.flight_id)].map((id) => id?.toLowerCase())).size !== v.connections.length + 1)
		ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["connections"], message: m.connectionsDuplicate ?? enTravelFormMessages.connectionsDuplicate! });
	if (!transportTypeFits(v.mode, v.transport_type))
		ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["transport_type"], message: m.transportTypeMismatch ?? enTravelFormMessages.transportTypeMismatch! });
	else if (v.transport_type === "OTHER" && v.transport_type_other === null)
		ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["transport_type_other"], message: m.transportTypeOtherRequired ?? enTravelFormMessages.transportTypeOtherRequired! });
	if (legNeedsFlightNumber(v.mode, v.transport_type) && v.flight_id === null && v.service_no === null)
		ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["service_no"], message: m.flightNumberRequired ?? m.required });
	if (v.flight_id !== null && v.mode !== "AIR") ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["flight_id"], message: m.flightOnlyForAir });
	if (v.flight_id !== null && v.delayed_to !== null) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["delayed_to"], message: m.delayWithFlight });
};

/**
 * One leg, as nested in `POST /accreditations` / `PATCH /accreditations/:id`
 * (`arrival` / `departure`; `null` there removes the leg) and in
 * `POST /travel/bulk` (`leg`). Always the whole leg.
 */
export function makeTravelLegFields(m: M = enTravelFormMessages) {
	return z.object(legShape(m)).superRefine(refineLeg(m));
}
export const travelLegFields = makeTravelLegFields();
export type TravelLegBody = z.input<typeof travelLegFields>;
export type TravelLegInput = z.output<typeof travelLegFields>;

/**
 * `PUT /accreditations/:id/travel/:direction` — create or replace the card's
 * leg. `version` = the leg's version the client read (absent when creating);
 * a different stored version is 409 STALE_VERSION. `DELETE` of the same path
 * removes it.
 */
export function makeTravelLegForm(m: M = enTravelFormMessages) {
	return z.object({ ...legShape(m), version: z.number().int().min(1).optional() }).superRefine(refineLeg(m));
}
export const putTravelLegBody = makeTravelLegForm();
export type PutTravelLegBody = z.input<typeof putTravelLegBody>;

/** The path params of `/accreditations/:id/travel/:direction`; `direction` comes out upper-case. */
export const travelLegParams = z.object({
	id: uuid,
	direction: z.enum([TRAVEL_DIRECTION_SEGMENTS.ARRIVAL, TRAVEL_DIRECTION_SEGMENTS.DEPARTURE]).transform((s): TravelDirection => (s === "arrival" ? "ARRIVAL" : "DEPARTURE")),
});

/** Both legs in one body (create / PATCH): a departure dated before the arrival is refused. */
export const departureNotBeforeArrival = (v: { arrival?: { travel_date: string } | null; departure?: { travel_date: string } | null }): boolean =>
	!v.arrival || !v.departure || v.departure.travel_date >= v.arrival.travel_date;

// ── Bulk ────────────────────────────────────────────────────────────────────

/**
 * `POST /travel/bulk` — "Add this arrival for N records": the same leg for
 * every card in `card_ids`. A card that already has a leg in that direction
 * keeps it unless `replace_existing` (a bulk overwrite of 500 people's
 * tickets is one misclick away, so it is opt-in); such cards are refused as
 * `already_set`.
 */
export function makeTravelBulkForm(m: M = enTravelFormMessages) {
	return z.object({
		card_ids: z
			.array(uuid)
			.min(1, m.required)
			.max(TRAVEL_BULK_MAX)
			.refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length, m.idsDuplicate),
		direction: z.enum(TRAVEL_DIRECTIONS),
		leg: makeTravelLegFields(m),
		replace_existing: z.boolean().default(false),
	});
}
export const travelBulkBody = makeTravelBulkForm();
export type TravelBulkBody = z.input<typeof travelBulkBody>;

/**
 * Why a card was left alone by `POST /travel/bulk`:
 *   not_in_scope  not visible to the caller, or does not exist
 *   permission    visible, but the caller may not write its travel
 *   locked        the card is REJECTED or WITHDRAWN (travel closed), or —
 *                 for a register-only writer — no longer editable
 *   already_set   it has a leg in that direction and `replace_existing` was false
 */
export const TRAVEL_BULK_REFUSAL_REASONS = ["not_in_scope", "permission", "locked", "already_set"] as const;
export type TravelBulkRefusalReason = (typeof TRAVEL_BULK_REFUSAL_REASONS)[number];

export interface TravelBulkResult {
	/** Legs created or replaced. */
	saved: number;
	refused: Array<{ ac_card_id: string; reason: TravelBulkRefusalReason }>;
}

// ── Reads: groups, legs, summary ────────────────────────────────────────────

/** `status=DELAYED,CANCELLED` — one or more flight statuses, comma-separated, de-duplicated. */
const flightStatusList = z
	.string()
	.max(200)
	.transform((v) =>
		v
			.split(",")
			.map((s) => s.trim())
			.filter((s) => s.length > 0),
	)
	.pipe(z.array(z.enum(FLIGHT_STATUSES)).min(1))
	.transform((list) => [...new Set(list)]);
/** Shared with `travel-journeys.ts`. */
export const flightStatusListParam = flightStatusList;

/**
 * The filters `/travel/groups` and `/travel/legs` share. `games_id` is
 * required: the legs index leads with it, and `from` / `to` are dates in
 * that games' time zone. `to` defaults to `from`.
 *
 * `status` filters by flight status; a leg with no flight row counts as
 * SCHEDULED, or DELAYED when its `delayed_to` is set.
 */
function rangeShape(m: M) {
	return {
		games_id: uuid,
		direction: z.enum(TRAVEL_DIRECTIONS),
		from: dateField(m),
		to: dateField(m).optional(),
		mode: z.enum(TRAVEL_MODES).optional(),
		entry_point_id: uuid.optional(),
		responsible_organization_id: uuid.optional(),
		status: flightStatusList.optional(),
		/** Groups: a flight number, service no. or origin. Legs: also a name or card number. */
		q: searchParam.optional(),
	};
}

const refineRange = (m: M) => (v: { from: string; to?: string | undefined }, ctx: z.RefinementCtx) => {
	if (v.to === undefined) return;
	const days = daysBetween(v.from, v.to);
	if (days < 0) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: m.rangeInvalid });
	else if (days + 1 > TRAVEL_GROUP_RANGE_DAYS_MAX) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: m.rangeTooLong });
};

/**
 * `GET /travel/groups` — legs in scope in the range, grouped: by flight, or
 * for legs with no flight row by entry point + mode + scheduled hour +
 * service no. ONE query, at most TRAVEL_GROUPS_LIMIT groups (`truncated`).
 */
export function makeTravelGroupsQuery(m: M = enTravelFormMessages) {
	return z.object(rangeShape(m)).superRefine(refineRange(m));
}
export const travelGroupsQuery = makeTravelGroupsQuery();
export type TravelGroupsQuery = z.output<typeof travelGroupsQuery>;
export type TravelGroupsQueryInput = z.input<typeof travelGroupsQuery>;

/**
 * `GET /travel/legs` — one row per person, keyset on `(expected_at,
 * travel_leg_id)` ascending, NULLS LAST. `cursor` is opaque: send back
 * `next_cursor` exactly as received. The total comes with the first page
 * (no cursor) only. `group` = a group's `group_key`; `flight_id` = every leg
 * on that flight.
 *
 * M9b: `from` is required UNLESS `group` is set — a journey's key bounds the
 * walk itself (a flight; a service slot's local day), so the journey page
 * lists its people without a range. `to` without `from` is refused.
 * `readiness` keeps the legs failing that atom (TRAVEL_READINESS_ATOMS; the
 * same predicate as `/travel/journeys?not_ready=`).
 */
export function makeTravelLegsQuery(m: M = enTravelFormMessages) {
	return z
		.object({
			...rangeShape(m),
			from: dateField(m).optional(),
			flight_id: uuid.optional(),
			group: z.string().trim().min(1).max(TRAVEL_GROUP_KEY_MAX).optional(),
			readiness: z.enum(TRAVEL_READINESS_ATOMS).optional(),
			/**
			 * `true`: only legs riding NO live transfer, whether or not one was
			 * asked for (`needs_transport`) — the logistics desk's "no transfer
			 * yet" (user, 2026-10-31: an arrival nobody asked a transfer for is
			 * still the host's to offer one).
			 */
			no_transfer: booleanParam.optional(),
			cursor: z.string().min(1).max(200).optional(),
			limit: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(TRAVEL_LEGS_PAGE_SIZE_DEFAULT),
		})
		.superRefine((v, ctx) => {
			if (v.from === undefined) {
				if (v.group === undefined || v.to !== undefined) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["from"], message: m.required });
				return;
			}
			refineRange(m)({ from: v.from, to: v.to }, ctx);
		});
}
export const travelLegsQuery = makeTravelLegsQuery();
export type TravelLegsQuery = z.output<typeof travelLegsQuery>;
export type TravelLegsQueryInput = z.input<typeof travelLegsQuery>;

/** `GET /travel/summary` — the A&D Overview tiles; `date` defaults to today in the games' time zone. */
export const travelSummaryQuery = z.object({
	games_id: uuid,
	date: dateField(enTravelFormMessages).optional(),
});
export type TravelSummaryQuery = z.output<typeof travelSummaryQuery>;

// ── Flights ─────────────────────────────────────────────────────────────────

/**
 * What `date` means on `GET /flights/lookup`:
 *   LEG      the leg's travel date (local); for an ARRIVAL the flight may have
 *            left the day before (default — typed dates)
 *   SERVICE  the flight's scheduled DEPARTURE date, local at the origin
 *            (`flights.service_date`) — a date picked from `GET /flights/dates`
 */
export const FLIGHT_LOOKUP_DATE_KINDS = ["LEG", "SERVICE"] as const;
export type FlightLookupDateKind = (typeof FLIGHT_LOOKUP_DATE_KINDS)[number];

const flightDesignatorParam = (m: M) =>
	z
		.string()
		.trim()
		.min(1, m.flightInvalid)
		.max(FLIGHT_DESIGNATOR_INPUT_MAX, m.flightInvalid)
		.refine((v) => parseFlightDesignator(v) !== null, m.flightInvalid);

/**
 * `GET /flights/lookup?flight=MH803&date=2026-11-03&direction=ARRIVAL[&date_kind=SERVICE][&role=CONNECTION]`.
 * `direction` is the LEG's direction for a connection too (it decides how `date` is read).
 * Rate-limited per login (`FLIGHT_LOOKUP_MAX` per hour).
 */
export function makeFlightLookupQuery(m: M = enTravelFormMessages) {
	return z
		.object({
			flight: flightDesignatorParam(m),
			date: dateField(m),
			direction: z.enum(TRAVEL_DIRECTIONS),
			date_kind: z.enum(FLIGHT_LOOKUP_DATE_KINDS).default("LEG"),
			/** MAIN (default) or CONNECTION — see FLIGHT_LOOKUP_ROLES. */
			role: z.enum(FLIGHT_LOOKUP_ROLES).default("MAIN"),
		})
		.transform(({ date_kind, ...rest }) => ({ ...rest, dateKind: date_kind }));
}
export const flightLookupQuery = makeFlightLookupQuery();
export type FlightLookupQuery = z.output<typeof flightLookupQuery>;

/**
 * `GET /flights/dates?flight=MH602&direction=ARRIVAL` — the days this flight
 * number operates (scheduled DEPARTURE dates, local at the origin) inside the
 * games' travel window. Cached server-side per flight number; only a cache
 * miss reaches the provider and counts against `FLIGHT_LOOKUP_MAX`.
 */
export function makeFlightDatesQuery(m: M = enTravelFormMessages) {
	return z.object({
		flight: flightDesignatorParam(m),
		direction: z.enum(TRAVEL_DIRECTIONS),
	});
}
export const flightDatesQuery = makeFlightDatesQuery();
export type FlightDatesQuery = z.output<typeof flightDatesQuery>;

/**
 * `PATCH /flights/:id` (host desk: `logistics.travel_review`, or admin) —
 * set the times / status by hand. Any subset; `null` clears an estimate or an
 * actual. The flight's `tracking` becomes MANUAL (the tracker leaves it
 * alone) until `POST /flights/:id/resume-tracking`. Every passenger's
 * `expected_at` follows in one statement (trigger).
 */
export function makeFlightOverrideForm(m: M = enTravelFormMessages) {
	const dt = z.string().trim().datetime({ offset: true, message: m.dateTimeInvalid }).nullable().optional();
	return z
		.object({
			status: z.enum(FLIGHT_STATUSES).optional(),
			estimated_departure_at: dt,
			estimated_arrival_at: dt,
			actual_departure_at: dt,
			actual_arrival_at: dt,
			reason: z.string().trim().min(1).max(TRAVEL_REASON_MAX, m.textTooLong).optional(),
		})
		.refine(({ reason: _r, ...rest }) => Object.values(rest).some((x) => x !== undefined), { message: m.nothingToChange })
		.refine((v) => !v.actual_departure_at || !v.actual_arrival_at || Date.parse(v.actual_arrival_at) > Date.parse(v.actual_departure_at), {
			path: ["actual_arrival_at"],
			message: m.landedBeforeDeparted,
		});
}
export const flightOverrideBody = makeFlightOverrideForm();
export type FlightOverrideBody = z.input<typeof flightOverrideBody>;

// ── Entry points ────────────────────────────────────────────────────────────

export const ENTRY_POINT_SORT_KEYS = ["sort_order", "code", "name", "type", "created_at"] as const;
export type EntryPointSortKey = (typeof ENTRY_POINT_SORT_KEYS)[number];

/** `GET /entry-points` — admin list, offset paging (a small table). */
export const listEntryPointsQuery = z.object({
	page: z.coerce.number().int().min(1).default(1),
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(ENTRY_POINT_PAGE_SIZE_DEFAULT),
	/** Matches code, name and IATA code. */
	search: searchParam.optional(),
	type: z.enum(ENTRY_POINT_TYPES).optional(),
	is_active: booleanParam.optional(),
	sort: z.enum(ENTRY_POINT_SORT_KEYS).optional(),
	dir: z.enum(["asc", "desc"]).optional(),
});
export type ListEntryPointsQuery = z.output<typeof listEntryPointsQuery>;
export type ListEntryPointsQueryInput = z.input<typeof listEntryPointsQuery>;

/** `KLIA2`, `JHB-CIQ`: trimmed, upper-cased, letters / digits / - / _. Unique. */
const entryPointCode = (m: M) =>
	z
		.string()
		.trim()
		.toUpperCase()
		.min(1, m.codeInvalid)
		.max(ENTRY_POINT_CODE_MAX, m.codeInvalid)
		.regex(ENTRY_POINT_CODE_PATTERN, m.codeInvalid);
const entryPointName = (m: M) => z.string().trim().min(1, m.nameRequired).max(ENTRY_POINT_NAME_MAX, m.textTooLong);
/** KLIA1 and KLIA2 are both `KUL`: not unique. */
const iataCode = (m: M) => z.string().trim().toUpperCase().regex(IATA_CODE_PATTERN, m.iataInvalid);

export const ENTRY_POINT_TYPE_IMMUTABLE_MESSAGE = "An entry point's type cannot be changed. Create a new entry point instead.";

/** `POST /entry-points` (SUPER_ADMIN). */
export function makeCreateEntryPointRequest(m: M = enTravelFormMessages) {
	return z
		.object({
			code: entryPointCode(m),
			name: entryPointName(m),
			type: z.enum(ENTRY_POINT_TYPES, { errorMap: () => ({ message: m.required }) }),
			iata_code: iataCode(m).nullable().optional(),
			terminal: patchText(m, ENTRY_POINT_TERMINAL_MAX),
			/** The welcome desk where cards are handed over. */
			card_handover_location_id: uuid.nullable().optional(),
			/** M10: the transport cluster (drive times from the airport). */
			cluster_id: uuid.nullable().optional(),
			dwell_minutes: minutes(m, ENTRY_POINT_DWELL_MINUTES_MIN, ENTRY_POINT_DWELL_MINUTES_MAX).default(ENTRY_POINT_DWELL_MINUTES_DEFAULT),
			departure_lead_minutes: minutes(m, ENTRY_POINT_LEAD_MINUTES_MIN, ENTRY_POINT_LEAD_MINUTES_MAX).default(ENTRY_POINT_LEAD_MINUTES_DEFAULT),
			/** M9b: people the welcome desk processes per hour; omitted or null = not known. */
			desk_capacity_per_hour: deskCapacity(m).nullable().optional(),
			sort_order: sortOrder(m).default(0),
			is_active: z.boolean().default(true),
		})
		.refine((v) => v.type === "AIR" || v.iata_code == null, { path: ["iata_code"], message: m.iataOnlyForAir });
}
export const createEntryPointBody = makeCreateEntryPointRequest();
export type CreateEntryPointBody = z.input<typeof createEntryPointBody>;

/**
 * `PATCH /entry-points/:id` (SUPER_ADMIN) — any subset; `null` clears. The
 * `type` is fixed at creation (legs' `mode` must equal it): a body that sends
 * one is REFUSED, not stripped. An `iata_code` on a non-AIR entry point is
 * refused by the database CHECK (the schema cannot see the stored type).
 */
export function makePatchEntryPointRequest(m: M = enTravelFormMessages) {
	return z
		.object({
			code: entryPointCode(m).optional(),
			name: entryPointName(m).optional(),
			type: z.never({ errorMap: () => ({ message: ENTRY_POINT_TYPE_IMMUTABLE_MESSAGE }) }).optional(),
			iata_code: iataCode(m).nullable().optional(),
			terminal: patchText(m, ENTRY_POINT_TERMINAL_MAX),
			card_handover_location_id: uuid.nullable().optional(),
			cluster_id: uuid.nullable().optional(),
			dwell_minutes: minutes(m, ENTRY_POINT_DWELL_MINUTES_MIN, ENTRY_POINT_DWELL_MINUTES_MAX).optional(),
			departure_lead_minutes: minutes(m, ENTRY_POINT_LEAD_MINUTES_MIN, ENTRY_POINT_LEAD_MINUTES_MAX).optional(),
			/** M9b: `null` clears it. */
			desk_capacity_per_hour: deskCapacity(m).nullable().optional(),
			sort_order: sortOrder(m).optional(),
			is_active: z.boolean().optional(),
			/** Headline for this change in the Activity feed. */
			reason: z.string().trim().min(1).max(TRAVEL_REASON_MAX, m.textTooLong).optional(),
		})
		.refine(({ reason: _r, ...rest }) => Object.values(rest).some((x) => x !== undefined), { message: m.nothingToChange });
}
export const patchEntryPointBody = makePatchEntryPointRequest();
export type PatchEntryPointBody = z.input<typeof patchEntryPointBody>;

/**
 * The Game Settings form (inputs as they bind: `""` = empty). `type` is
 * shown read-only when editing. `toCreateEntryPointBody` /
 * `toUpdateEntryPointBody` build the wire bodies.
 */
export function makeEntryPointForm(m: M = enTravelFormMessages) {
	return z
		.object({
			code: entryPointCode(m),
			name: entryPointName(m),
			type: z.enum(ENTRY_POINT_TYPES, { errorMap: () => ({ message: m.required }) }),
			iata_code: z.string().trim().toUpperCase().refine((v) => v === "" || IATA_CODE_PATTERN.test(v), m.iataInvalid),
			terminal: z.string().trim().max(ENTRY_POINT_TERMINAL_MAX, m.textTooLong),
			card_handover_location_id: z.string().refine((v) => v === "" || uuid.safeParse(v).success, m.required),
			/** M10: `""` = none; ABSENT = this form does not manage the cluster (the update body leaves it alone). */
			cluster_id: z
				.string()
				.refine((v) => v === "" || uuid.safeParse(v).success, m.clusterInvalid ?? "Pick a cluster from the list")
				.optional(),
			dwell_minutes: z.coerce.number({ invalid_type_error: m.minutesInvalid }).pipe(minutes(m, ENTRY_POINT_DWELL_MINUTES_MIN, ENTRY_POINT_DWELL_MINUTES_MAX)),
			departure_lead_minutes: z.coerce.number({ invalid_type_error: m.minutesInvalid }).pipe(minutes(m, ENTRY_POINT_LEAD_MINUTES_MIN, ENTRY_POINT_LEAD_MINUTES_MAX)),
			/** M9b: `""` = not known; ABSENT = this form does not manage it (the update body leaves it alone). */
			desk_capacity_per_hour: z
				.string()
				.trim()
				.refine((v) => v === "" || deskCapacity(m).safeParse(Number(v)).success, m.deskCapacityInvalid ?? enTravelFormMessages.deskCapacityInvalid!)
				.optional(),
			sort_order: z.coerce.number({ invalid_type_error: m.sortOrderInvalid }).pipe(sortOrder(m)),
			is_active: z.boolean(),
		})
		.refine((v) => v.type === "AIR" || v.iata_code === "", { path: ["iata_code"], message: m.iataOnlyForAir });
}
export const entryPointForm = makeEntryPointForm();
export type EntryPointForm = z.output<typeof entryPointForm>;

export const toCreateEntryPointBody = (f: EntryPointForm): CreateEntryPointBody => ({
	code: f.code,
	name: f.name,
	type: f.type,
	...(f.iata_code ? { iata_code: f.iata_code } : {}),
	...(f.terminal ? { terminal: f.terminal } : {}),
	...(f.card_handover_location_id ? { card_handover_location_id: f.card_handover_location_id } : {}),
	...(f.cluster_id ? { cluster_id: f.cluster_id } : {}),
	...(f.desk_capacity_per_hour ? { desk_capacity_per_hour: Number(f.desk_capacity_per_hour) } : {}),
	dwell_minutes: f.dwell_minutes,
	departure_lead_minutes: f.departure_lead_minutes,
	sort_order: f.sort_order,
	is_active: f.is_active,
});

/** Only what actually changed, so the audit row's `changed_fields` names the edit. Never `type`. */
export function toUpdateEntryPointBody(f: EntryPointForm, before: EntryPoint): PatchEntryPointBody {
	const body: PatchEntryPointBody = {};
	const orNull = (v: string) => (v === "" ? null : v);
	if (f.code !== before.code) body.code = f.code;
	if (f.name !== before.name) body.name = f.name;
	if (orNull(f.iata_code) !== before.iata_code) body.iata_code = orNull(f.iata_code);
	if (orNull(f.terminal) !== before.terminal) body.terminal = orNull(f.terminal);
	const desk = orNull(f.card_handover_location_id);
	if (desk !== (before.card_handover_location?.card_handover_location_id ?? null)) body.card_handover_location_id = desk;
	if (f.cluster_id !== undefined && orNull(f.cluster_id) !== before.cluster_id) body.cluster_id = orNull(f.cluster_id);
	if (f.desk_capacity_per_hour !== undefined) {
		const cap = f.desk_capacity_per_hour === "" ? null : Number(f.desk_capacity_per_hour);
		if (cap !== before.desk_capacity_per_hour) body.desk_capacity_per_hour = cap;
	}
	if (f.dwell_minutes !== before.dwell_minutes) body.dwell_minutes = f.dwell_minutes;
	if (f.departure_lead_minutes !== before.departure_lead_minutes) body.departure_lead_minutes = f.departure_lead_minutes;
	if (f.sort_order !== before.sort_order) body.sort_order = f.sort_order;
	if (f.is_active !== before.is_active) body.is_active = f.is_active;
	return body;
}

// ── Responses ───────────────────────────────────────────────────────────────

/** An entry point as other payloads mention it; also one row of `GET /entry-points/options` (active rows, in sort order). */
export interface EntryPointRef {
	entry_point_id: string;
	code: string;
	name: string;
	type: EntryPointType;
	iata_code: string | null;
	terminal: string | null;
	dwell_minutes: number;
	departure_lead_minutes: number;
	/** M9b: people the welcome desk processes per hour; null = not known. */
	desk_capacity_per_hour: number | null;
}

/** `GET /entry-points/options` — any signed-in GMS role, for the leg form's picker. */
export type EntryPointOption = EntryPointRef;

/** One row of `GET /entry-points`; also the detail and what a write returns. */
export interface EntryPoint extends EntryPointRef {
	card_handover_location: { card_handover_location_id: string; code: string | null; name: string } | null;
	/** M10: the transport cluster, or null (transfers then take the cluster from the other end). */
	cluster_id: string | null;
	cluster: ClusterRef | null;
	sort_order: number;
	/** Hidden from pickers when false. */
	is_active: boolean;
	/** Legs that use it: a delete is refused (409 CONFLICT) while > 0. */
	leg_count: number;
	created_at: string;
	updated_at: string;
}

/** A flight as legs and groups carry it. `expected_*` = actual ?? estimated ?? scheduled (generated columns). */
export interface FlightSummary {
	flight_id: string;
	carrier_code: string;
	flight_number: string;
	/** Scheduled departure date, local at the origin: the provider's identity of the flight. */
	service_date: string;
	origin_iata: string | null;
	/** The provider's name for the origin ("Manila"), else the `airports` row's city, else its name. */
	origin_name: string | null;
	/**
	 * IANA zone of the origin airport (`airports.time_zone`, joined by IATA in
	 * the reading statement); null when the airport is unknown or has no zone.
	 * The flight card prints each end in its airport's own time (user,
	 * 2026-11-03 — the card only; everything else stays viewer-local).
	 */
	origin_time_zone: string | null;
	destination_iata: string | null;
	/** The destination airport's city, else its name (`airports`); null when unknown. */
	destination_name: string | null;
	/** IANA zone of the destination airport, as `origin_time_zone`. */
	destination_time_zone: string | null;
	destination_terminal: string | null;
	scheduled_departure_at: string | null;
	scheduled_arrival_at: string | null;
	expected_departure_at: string | null;
	expected_arrival_at: string | null;
	status: FlightStatus;
	tracking: FlightTracking;
}

/** The whole flight: `GET /flights/lookup`, `PATCH /flights/:id`, resume-tracking. */
export interface Flight extends FlightSummary {
	estimated_departure_at: string | null;
	estimated_arrival_at: string | null;
	actual_departure_at: string | null;
	actual_arrival_at: string | null;
	last_checked_at: string | null;
	next_check_at: string | null;
	/** The last poll's failure, kept until a poll succeeds. */
	check_error: string | null;
	created_at: string;
	updated_at: string;
}

/**
 * `GET /flights/lookup`. A hit stores (or refreshes) the flight row and
 * suggests the entry point whose IATA code + terminal match the flight's
 * destination (ARRIVAL) or origin (DEPARTURE).
 */
export interface FlightLookupResult {
	found: boolean;
	/** Only when `found` is false. */
	reason?: FlightLookupMissReason;
	flight: Flight | null;
	suggested_entry_point_id: string | null;
	/** Only with NOT_OUR_AIRPORT: the leg the provider found (the one that would have been picked). */
	route?: FlightLookupRoute;
	/** Only with NOT_OUR_AIRPORT: our active AIR entry points' IATA codes, ascending, distinct. */
	airports?: string[];
}

/** Where a flight the lookup refused actually flies (NOT_OUR_AIRPORT). Instants are ISO 8601 UTC. */
export interface FlightLookupRoute {
	origin_iata: string | null;
	origin_name: string | null;
	destination_iata: string | null;
	destination_name: string | null;
	scheduled_departure_at: string | null;
	scheduled_arrival_at: string | null;
}

/**
 * `GET /flights/dates`. `found: false` (TRACKING_OFF, or NOT_FOUND — e.g. a
 * charter the schedules do not carry) means: fall back to a typed date.
 * `dates` are ascending `YYYY-MM-DD` departure dates within `from`..`to`.
 */
export interface FlightDatesResult {
	found: boolean;
	/** Only when `found` is false. */
	reason?: FlightLookupMissReason;
	/** Normalised designator, e.g. `MH 602`. */
	flight: string;
	dates: string[];
	/** The window asked of the provider, both ends included. */
	from: string;
	to: string;
}

/** One leg of a card: `GET /accreditations/:id` → `travel.arrival` / `travel.departure`, and what a leg write answers. */
export interface TravelLeg {
	travel_leg_id: string;
	ac_card_id: string;
	direction: TravelDirection;
	mode: TravelMode;
	/** How they travel within the mode; null on legs saved before it was asked. */
	transport_type: TravelTransportType | null;
	/** OTHER: what it is, as typed; else null. */
	transport_type_other: string | null;
	entry_point: EntryPointRef;
	travel_date: string;
	scheduled_at: string | null;
	delayed_to: string | null;
	/** The flight's expected arrival (ARRIVAL) / departure (DEPARTURE) when on a flight, else `delayed_to ?? scheduled_at`. Kept by a trigger. */
	expected_at: string | null;
	flight: FlightSummary | null;
	/** Transit: the connecting flights, in travel order (ARRIVAL: flown before `flight`; DEPARTURE: after it). `[]` = direct. */
	connections: FlightSummary[];
	/** Computed on read: a change shorter than MIN_CONNECTION_MINUTES on expected times, or a connection CANCELLED / DIVERTED (`isConnectionAtRisk`). */
	connection_at_risk: boolean;
	service_no: string | null;
	origin: string | null;
	needs_transport: boolean;
	remarks: string | null;
	version: number;
	created_at: string;
	updated_at: string;
}

/** The flights of a leg in travel order: ARRIVAL = connections then the main flight; DEPARTURE = the main flight then connections. */
export function legFlightChain<T>(direction: TravelDirection, main: T | null, connections: readonly T[]): T[] {
	if (main === null) return [...connections];
	return direction === "ARRIVAL" ? [...connections, main] : [main, ...connections];
}

/** Statuses of a connecting flight that put the trip at risk whatever the times say. */
export const CONNECTION_BROKEN_STATUSES = ["CANCELLED", "DIVERTED"] as const satisfies readonly FlightStatus[];

/**
 * The `connection_at_risk` rule, as the API computes it in SQL
 * (`connectionRiskSql`, travel-shapes.ts; an integration test pins both):
 * over the flights of a leg in TRAVEL ORDER (`legFlightChain`), true when a
 * CONNECTION (not the main flight) is CANCELLED or DIVERTED, or for any
 * consecutive pair the earlier flight's expected arrival + MIN_CONNECTION_MINUTES
 * is after the later flight's expected departure. A missing time decides
 * nothing. A direct leg (no connections) is never at risk.
 */
export function isConnectionAtRisk(
	chain: ReadonlyArray<Pick<FlightSummary, "expected_departure_at" | "expected_arrival_at" | "status"> & { connection: boolean }>,
): boolean {
	if (!chain.some((f) => f.connection)) return false;
	if (chain.some((f) => f.connection && (CONNECTION_BROKEN_STATUSES as readonly FlightStatus[]).includes(f.status))) return true;
	for (let i = 1; i < chain.length; i++) {
		const landed = chain[i - 1]!.expected_arrival_at;
		const leaves = chain[i]!.expected_departure_at;
		if (landed === null || leaves === null) continue;
		if (Date.parse(landed) + MIN_CONNECTION_MINUTES * 60_000 > Date.parse(leaves)) return true;
	}
	return false;
}

/** `GET /accreditations/:id` → `travel`. */
export interface AccreditationTravel {
	arrival: TravelLeg | null;
	departure: TravelLeg | null;
	/**
	 * GMS-2: the live transfer carrying each leg (`transport_request_passengers`,
	 * not released), or null — the record's logistics chain (Travel → Transfer
	 * → Room). In the reader's view, as A&D's `TravelLegRow.transfer`: a NOC
	 * never sees SHORT, and a host trip's id / ref are null to it.
	 */
	arrival_transfer: TravelLegTransfer | null;
	departure_transfer: TravelLegTransfer | null;
}

/** One row of `GET /travel/groups`: a flight, or a land / sea service at one hour. */
export interface TravelGroup {
	/** Opaque; pass back as `/travel/legs?group=`. */
	group_key: string;
	direction: TravelDirection;
	mode: TravelMode;
	flight: FlightSummary | null;
	entry_point: EntryPointRef;
	service_no: string | null;
	/**
	 * The OTHER end of the trip, as `TravelLeg.origin` means it: where they come
	 * FROM for an ARRIVAL, where they go TO for a DEPARTURE. The legs' own
	 * `origin` when they share one; else, on a flight, its origin name (ARRIVAL)
	 * or destination IATA code (DEPARTURE — flights carry no destination name);
	 * else null.
	 */
	origin: string | null;
	travel_date: string;
	/** Earliest among the group's legs. */
	scheduled_at: string | null;
	expected_at: string | null;
	/** Legs in the group the caller can see. */
	people: number;
	/** Of those, on APPROVED cards (transport and accommodation act on these only). */
	approved: number;
	needs_transport: number;
	/** At most TRAVEL_GROUP_ORGS_MAX, by size; the rest counted in `organizations_more`. */
	organizations: AccreditationOrgRef[];
	organizations_more: number;
	/**
	 * Legs of the group running late: a manual delay set (`delayed_to`), or
	 * the flight expected ≥ FLIGHT_DELAY_THRESHOLD_MINUTES after its schedule.
	 * "Delayed · 16 of 17" = `delayed_count` of `people`.
	 */
	delayed_count: number;
	/** Legs of the group whose connection is at risk (`TravelLeg.connection_at_risk`). */
	connection_at_risk_count: number;
	/** Earliest and latest `expected_at` among the group's legs (null when none has a time) — a group whose legs split over a delay spans them. */
	expected_from: string | null;
	expected_to: string | null;
}

/** `GET /travel/groups`. `truncated`: more than TRAVEL_GROUPS_LIMIT groups matched — narrow the range or filter. */
export interface TravelGroupsResult {
	items: TravelGroup[];
	truncated: boolean;
}

/** One row of `GET /travel/legs`: a person on a leg. */
export interface TravelLegRow {
	travel_leg_id: string;
	ac_card_id: string;
	direction: TravelDirection;
	mode: TravelMode;
	/** As `TravelLeg.transport_type` / `transport_type_other`. */
	transport_type: TravelTransportType | null;
	transport_type_other: string | null;
	registration_no_display: string;
	accreditation_no_display: string | null;
	card_status: AcCardStatus;
	person: {
		people_id: string;
		/** As it prints on the card. */
		printed_name: string;
		country_code: string;
		gender: string | null;
	};
	responsible_organization: AccreditationOrgRef;
	main_function: AccreditationCategoryRef;
	/** The card's function (sub function), when set. */
	sub_function: AccreditationRef | null;
	/** Someone else changed this card or its person since this login last opened it (contracts `seen.ts`). */
	changed: boolean;
	/** Presigned GET of the photo thumbnail, or null (the records list's helper). */
	photo_thumb_url: string | null;
	entry_point: EntryPointRef;
	travel_date: string;
	scheduled_at: string | null;
	delayed_to: string | null;
	expected_at: string | null;
	flight: FlightSummary | null;
	/** As `TravelLeg.connections`. */
	connections: FlightSummary[];
	/** As `TravelLeg.connection_at_risk`. */
	connection_at_risk: boolean;
	service_no: string | null;
	origin: string | null;
	needs_transport: boolean;
	remarks: string | null;
	/** M9b: `people.wheelchair_user`. */
	wheelchair: boolean;
	/** M9b: the card's open room assignment, or null (no bed). */
	room: TravelLegRoom | null;
	/** M9b: the leg's live transfer (`transport_request_passengers`, not released), or null. */
	transfer: TravelLegTransfer | null;
}

/** M9b: where a person on a leg sleeps (their open `room_assignments` row). */
export interface TravelLegRoom {
	room_no: string;
	building_name: string;
	/** Checked in at the building (`checked_in_at` set). */
	checked_in: boolean;
}

/**
 * M9b: a leg's live transfer. NOC view (ORGANIZATION): `status` is the NOC
 * variant (never SHORT), and `transport_request_id` / `ref` are null unless
 * the trip is the organisation's own (a host trip is never named to a NOC).
 */
export interface TravelLegTransfer {
	transport_request_id: string | null;
	/** `TRIP-000123` (`formatTripRef`), or null as above. */
	ref: string | null;
	status: TransportDisplayStatus;
	/**
	 * The driver is to be re-checked: the trip reads ASSIGNED but the delay
	 * cascade flagged it (`needs_recheck` / `needs_redispatch`) — the
	 * `recheck` readiness atom failing. Never "Driver assigned" then.
	 */
	recheck: boolean;
}

/** `GET /travel/legs` — keyset. `total` is set on the first page (no cursor) only, else null. */
export interface TravelLegsPage {
	items: TravelLegRow[];
	next_cursor: string | null;
	total: number | null;
}

export interface TravelDayCounts {
	people: number;
	groups: number;
}

/**
 * `GET /travel/summary` — ONE aggregate for the Overview tiles, in scope.
 * `day` = `date`, `next_day` = the day after (both in `timezone`).
 */
export interface TravelSummary {
	date: string;
	/** `games.timezone`: the A&D screens label times in it (MYT). */
	timezone: string;
	/** M9b: `day.landed` = people on `date` arrivals whose flight is LANDED. */
	arrivals: { day: TravelDayCounts & { landed: number }; next_day: TravelDayCounts };
	/** M9b: `day.departed` = people on `date` departures whose flight has DEPARTED (or LANDED). */
	departures: { day: TravelDayCounts & { departed: number }; next_day: TravelDayCounts };
	/** People on `day` / `next_day` legs whose flight is DELAYED (or whose `delayed_to` is set). */
	delayed: number;
	/** People on `day` / `next_day` legs whose flight is CANCELLED. */
	cancelled: number;
	/** APPROVED cards with no arrival leg (→ records list `travel=missing_arrival`). */
	approved_without_arrival: number;
	approved_without_departure: number;
	/** M9b: organisations with at least one `approved_without_arrival` card. */
	orgs_without_arrival: number;
	/** People per entry point, direction and local hour (0–23) on `day`; zero rows omitted. */
	hourly: Array<{ entry_point_id: string; direction: TravelDirection; hour: number; people: number }>;
}

// ── Manual delay of legs with no flight row (M9 S3) ─────────────────────────

/**
 * `POST /travel/delay` — the host desk sets (or, with `null`, clears) the
 * manual delay of coach / ferry / untracked-flight legs: `delayed_to` on every
 * leg in `leg_ids`, all of `games_id`. Legs ON a flight row take their delay
 * from the flight (`PATCH /flights/:id`) and are refused `has_flight`.
 *
 * Who: admins; a group with `logistics.travel_review` and a writable scope row
 * over the leg's card (the host's A&D desk); or whoever may write the leg
 * itself (`logistics.travel`, or `accreditation.register` while the card is
 * editable). Never on a REJECTED / WITHDRAWN card. ONE set-based UPDATE; a
 * leg already at that value is left alone and not refused. `reason` is the
 * Activity headline (default "Set a manual delay" / "Cleared the manual delay").
 */
export function makeTravelDelayForm(m: M = enTravelFormMessages) {
	return z.object({
		games_id: uuid,
		leg_ids: z
			.array(uuid)
			.min(1, m.required)
			.max(TRAVEL_BULK_MAX)
			.refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length, m.idsDuplicate),
		/** The new expected time; `null` clears the delay (the leg falls back to `scheduled_at`). Required key. */
		delayed_to: z.string().trim().datetime({ offset: true, message: m.dateTimeInvalid }).nullable(),
		reason: z.string().trim().min(1).max(TRAVEL_REASON_MAX, m.textTooLong).optional(),
	});
}
export const travelDelayBody = makeTravelDelayForm();
export type TravelDelayBody = z.input<typeof travelDelayBody>;

/**
 * Why `POST /travel/delay` left a leg alone:
 *   not_in_scope  not visible to the caller, not of `games_id`, or does not exist
 *   permission    visible, but the caller may not delay it
 *   locked        its card is REJECTED or WITHDRAWN
 *   has_flight    it rides a flight row: delay the flight instead
 */
export const TRAVEL_DELAY_REFUSAL_REASONS = ["not_in_scope", "permission", "locked", "has_flight"] as const;
export type TravelDelayRefusalReason = (typeof TRAVEL_DELAY_REFUSAL_REASONS)[number];

export interface TravelDelayResult {
	/** Legs whose `delayed_to` changed. */
	updated: number;
	refused: Array<{ travel_leg_id: string; reason: TravelDelayRefusalReason }>;
}
